import axios, { uiAPI } from 'lib/apiClient';
import qs from 'qs';
import get from 'lodash/get';
import assign from 'lodash/assign';
import uniqBy from 'lodash/uniqBy';
import { toUserTimezone } from 'utils';
import { addContactToGroup, removeContactFromGroup } from './contacts.service';
import { uploadMedia } from './media';

export const getGroupMembers = (query) => {
  return axios
    .get('API/1_0/getGroupContactList.php', {
      params: { load_last_steps: 1, ...query },
    })
    .then((resp) => resp.map((c) => ({ ...c, hash: c.contact_hash })));
};

export const getGroups = (query) =>
  axios.get('API/1_0/group.php', { params: query });

export const createGroup = async (data, isCopy) => {
  const api = isCopy ? 'postGroupDuplicate.php' : 'group.php';

  const group_reply_media = data.group_reply_media && data.group_reply_media[0];
  if (group_reply_media) {
    const { location } = await uploadMedia(group_reply_media);
    data.group_reply_media_url = location;
  }
  return axios
    .post(`API/1_0/${api}`, data)
    .then((resp) => (Array.isArray(resp) ? resp[0] : resp))
    .catch((e) => {
      if (e.message === 'There already exists a group with this keyword.') {
        throw new Error(
          'The keyword is already in use. Keywords must be unique for each group!',
        );
      }
      return Promise.reject(e);
    });
};

export const getGroup = (hash, params = {}) =>
  axios.get('API/1_0/group.php', {
    params: { hash, load_account_hash: 1, ...params },
  });

export const updateGroup = (hash, data) => {
  data.hash = hash;
  return createGroup(data);
};

export const deleteGroup = (hash) =>
  axios.delete('API/1_0/group.php', { params: { hash } });

export const getWorkflowSteps = (groupId, account_hash) =>
  axios.get('API/1_0/workflows.php', {
    params: {
      group_id: groupId,
      workflow_active: '-1',
      load_steps: 1,
      account_hash,
    },
  });

export const getTemplateWorkflows = (groupId, accountId) => {
  console.log('getTemplateWorkflows', groupId, accountId);
  return axios.get('API/1_0/workflows.php', {
    params: {
      group_id: groupId,
      account_id: accountId,
      workflow_active: '-1',
      load_steps: '1',
      templates: '1',
    },
  });
};

export const addMembersToGroup = (groupHash, ids) => {
  if (Array.isArray(groupHash)) {
    return Promise.all(groupHash.map((hash) => addMembersToGroup(hash, ids)));
  }
  return Promise.all(ids.map((id) => addContactToGroup(id, groupHash)));
};

export const removeMembers = (groupHash, contactHashes) => {
  return Promise.all(
    contactHashes.map((contactHash) =>
      removeContactFromGroup(contactHash, groupHash),
    ),
  );
};

export const getDCC = async (groupHash, contactHash) => {
  try {
    return axios
      .get(`API/1_0/getLegacyDcc.php`, {
        params: { group_hash: groupHash, contact_hash: contactHash },
      })
      .then((r) => {
        return r[0];
      });
  } catch (e) {
    throw new Error('Failed to fetch data');
  }
};

export const sendEmailInstruction = (payload) => {
  return axios.post('API/1_0/postRabbitMQ.php', payload);
};

export const duplicateGroup = (group_hash, props = {}) =>
  axios
    .post(`API/1_0/postGroupDuplicate.php`, { group_hash, ...props })
    .then((r) => r[0]);

export const getSyncLogs = (query) =>
  axios.get('API/1_0/groupIntegrationLog.php', {
    params: query,
  });

export const getGroupMessages = (query) =>
  axios.get('API/1_0/getGroupMessages.php', { params: query });

export const createDefaultGroups = async (groups) => {
  groups = groups || (await getDefaultGroups());
  return await Promise.all(
    groups.map((g) =>
      axios.post('API/1_0/postGroupDuplicate.php', { group_hash: g.hash }),
    ),
  );
};

export const getDefaultGroups = () =>
  axios.get('API/1_0/getGroupDefaultGroups.php?1=1');

export const getGroupUsers = (groupId) =>
  axios
    .get('API/1_0/userGroup.php', {
      params: { group_id: groupId, load_users: 1 },
    })
    .then((users) =>
      users.map((i) => ({
        ...i,
        user: i.users[0],
      })),
    );

export const getGroupsMembersCount = (group_hash) =>
  uiAPI.post('groups/contactsCount', {
    group_hash: Array.isArray(group_hash) ? group_hash : [group_hash],
  });

export const addGroupKeyword = (group_id, keyword, connections) =>
  axios.post(`API/1_0/groupKeyword.php`, {
    group_id,
    keyword,
    connections,
  });

export const getGroupKeyword = (group_hash, account_id) =>
  axios
    .get('API/1_0/groupKeyword.php', { params: { group_hash, account_id } })
    .then((r) => r[0]);

export const getGroupKeywords = (group_hash, account_id) =>
  axios.get('API/1_0/groupKeyword.php', { params: { group_hash, account_id } });

export const getKeywords = (keyword, account_id, connection_id) =>
  axios.get('API/1_0/groupKeyword.php', {
    params: { keyword, account_id, connection_id },
  });

export const updateKeyword = (hash, keyword, connections) =>
  axios.post(`API/1_0/groupKeyword.php`, {
    hash,
    keyword,
    connections,
  });

export const deleteKeyword = (hash) =>
  axios.delete(`API/1_0/groupKeyword.php`, {
    params: { hash },
  });

export const getGroupTags = (group_hash) =>
  axios.get(`API/1_0/groupTag.php`, { params: { group_hash } });

export const saveGroupTag = (data) => axios.post(`API/1_0/groupTag.php`, data);

export const deleteGroupTag = (hash) =>
  axios.delete(`API/1_0/groupTag.php`, { params: { hash } });

export const getTags = () =>
  axios
    .get(`API/1_0/groupTag.php`, { params: { f: 1 } })
    .then((resp) => uniqBy(resp, 'group_tag'));

export const getGroupsCategory = (params) =>
  axios.get(`API/1_0/categories.php`, {
    params,
  });

export const addGroupFolder = (data) =>
  axios.post(`API/1_0/categories.php`, data);

export const deleteCategory = (hash) =>
  axios.delete(`API/1_0/categories.php`, {
    params: { hash },
  });

export const createSubItem = (data) =>
  axios.post(`API/1_0/categoryItems.php`, data);

export const deleteSubCetegory = (hash) =>
  axios.delete(`API/1_0/categoryItems.php`, {
    params: { hash },
  });

// get all workflows
export const getWorkflows = (query) => {
  return axios.get('API/1_0/workflows.php', {
    params: query,
  });
};

export const getGroupWorkflowLastStep = (params) =>
  axios.get(`API/1_0/getGroupWorkflowLastScheduledStep.php`, {
    params,
  });

export const getGroupIntegrationLogStats = (params) =>
  axios.get(`API/1_0/getGroupIntegrationLogStats.php`, {
    params,
  });

export const postIntegrationLogRetry = (params) =>
  axios.post(`API/1_0/postIntegrationLogRetry.php`, params);

export const getKickstartGroups = (params) =>
  axios.get(`API/1_0/getKickStartGroups.php`, {
    params,
  });
